import { Avatar } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { withStyles, withTheme } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import CreateIcon from "@material-ui/icons/Create";
import LockIcon from "@material-ui/icons/Lock";
import moment from "moment-timezone";
import React, { Component } from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";
import { languages } from "../../constants/languages";
import DeleteFieldContainer from "../../containers/schedule/DeleteFieldContainer";
import EditBreakContainer from "../../containers/schedule/EditBreakContainer";
import EditFieldContainer from "../../containers/schedule/EditFieldContainer";
import Break from "./Break";
import Match from "./Match";

const MAX_SYNC_ATTEMPTS = 4;
const SYNC_RESET_COOLDOWN = 5 * 60 * 1000;

const StyledAvatar = withStyles({
  img: {
    height: "unset",
    borderRadius: 4,
  },
})(Avatar);

class Field extends Component {
  state = {
    open: false,
    anchorEl: null,
    editOpen: false,
    syncing: false,
    syncCount: 0,
  };

  // Live shows the cached st/et on each match, manage recomputes from the
  // field start time. A mismatch means the cache is stale and needs a
  // server-side recompute.
  isOutOfSync = () => {
    return this.props.matches.some(
      (m) => (!m.isBreak || m.isEvent) && m.startTime && m.startTime !== m.st,
    );
  };

  checkSyncStatus = () => {
    if (this.isOutOfSync()) {
      if (this.state.syncCount >= MAX_SYNC_ATTEMPTS) {
        // All attempts used, allow a new round after a cooldown
        if (!this.syncResetTimer) {
          this.syncResetTimer = setTimeout(() => {
            this.syncResetTimer = null;
            this.setState({ syncCount: 0 });
          }, SYNC_RESET_COOLDOWN);
        }
      } else if (!this.outOfSyncTimer && !this.state.syncing) {
        this.outOfSyncTimer = setTimeout(() => {
          this.outOfSyncTimer = null;
          this.handleSync();
        }, 3000);
      }
    } else {
      clearTimeout(this.outOfSyncTimer);
      this.outOfSyncTimer = null;
      clearTimeout(this.syncResetTimer);
      this.syncResetTimer = null;
      if (this.state.syncCount !== 0) {
        this.setState({ syncCount: 0 });
      }
    }
  };

  componentDidMount() {
    this.checkSyncStatus();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.syncing && !this.isOutOfSync()) {
      clearTimeout(this.syncTimeout);
      this.setState({ syncing: false });
    }

    if (
      prevState.syncing !== this.state.syncing ||
      prevState.syncCount !== this.state.syncCount ||
      prevProps.matches !== this.props.matches ||
      prevProps.field !== this.props.field ||
      prevProps.day !== this.props.day
    ) {
      this.checkSyncStatus();
    }
  }

  componentWillUnmount() {
    clearTimeout(this.syncTimeout);
    clearTimeout(this.outOfSyncTimer);
    clearTimeout(this.syncResetTimer);
  }

  handleSync = () => {
    if (this.state.syncing || this.state.syncCount >= MAX_SYNC_ATTEMPTS) return;

    console.info("sync requested");
    if (window.firebase.analytics) {
      window.firebase.analytics().logEvent("field_sync_request", {
        tournamentId: this.props.tournament[".key"],
        fieldId: this.props.field.id,
      });
    }

    const db = window.firebase.firestore();
    const batch = db.batch();
    batch.set(db.collection("cacheUpdatesForField").doc(), {
      tournamentId: this.props.tournament[".key"],
      fieldId: this.props.field.id,
      status: "pending",
      timestamp: window.firebase.firestore.FieldValue.serverTimestamp(),
    });
    batch.commit();

    this.setState((prev) => ({ syncing: true, syncCount: prev.syncCount + 1 }));
    this.syncTimeout = setTimeout(() => {
      if (window.firebase.analytics) {
        window.firebase.analytics().logEvent("field_sync_timeout", {
          tournamentId: this.props.tournament[".key"],
          fieldId: this.props.field.id,
        });
      }
      this.setState({ syncing: false });
    }, 5000);
  };

  toggleEditDialog = () => {
    if ((this.props.tournamentStatus || {}).closed) {
      this.props.toggleFinishedDialog();
    } else {
      if (this.state.editOpen) {
        this.handleClose();
      }
      this.setState({ editOpen: !this.state.editOpen });
    }
  };

  toggleClearDialog = () => {
    this.handleClose();
    this.setState({ open: !this.state.open });
  };

  handleMenu = (event) => {
    if ((this.props.tournamentStatus || {}).closed) {
      this.props.toggleFinishedDialog();
    } else {
      this.setState({ anchorEl: event.currentTarget });
    }
  };

  handleClose = () => {
    this.setState({ anchorEl: null });
  };

  handleClear = () => {
    window.firebase
      .firestore()
      .collection("tournaments")
      .doc(this.props.tournament[".key"])
      .collection("matches")
      .where("field", "==", this.props.field.id)
      .where("day", "==", this.props.day)
      .get()
      .then((querySnapshot) => {
        const db = window.firebase.firestore();
        let batch = db.batch();
        const matchesRef = db
          .collection("tournaments")
          .doc(this.props.tournament[".key"])
          .collection("matches");

        querySnapshot.forEach(function (doc) {
          batch.update(matchesRef.doc(doc.id), {
            field: null,
            day: "0",
            referee: "",
            referee2: "",
            referee3: "",
            referee4: "",
            referee5: "",
            st: "",
            et: "",
            duration: null,
          });
        });

        let breaks = this.props.tournament.breaks || {};
        let matchBreaks = this.props.matches.filter(
          (m) => m.isBreak && m.field === this.props.field.id,
        );
        for (let b in matchBreaks) {
          delete breaks[matchBreaks[b].id];
        }

        if (this.props.tournament.ia) {
          batch.set(db.collection("cacheUpdatesApp").doc(), {
            tournamentId: this.props.tournament[".key"],
            status: "pending",
            timestamp: window.firebase.firestore.FieldValue.serverTimestamp(),
          });
        }

        batch.update(
          db.collection("tournaments").doc(this.props.tournament[".key"]),
          { breaks: breaks },
        );
        batch.commit();
        this.toggleClearDialog();
      });
  };

  render() {
    let startTime = this.props.field.startTime;
    let endTime = this.props.field.endTime || "";
    if (this.props.day !== "0") {
      startTime =
        this.props.field[this.props.day] || this.props.field.startTime;
      endTime =
        this.props.field[this.props.day + "end"] !== undefined
          ? this.props.field[this.props.day + "end"]
          : this.props.field.endTime || "";
    }

    return (
      <div
        style={{
          marginBottom: 15,
          minWidth: 300,
          minHeight: 150,
          marginRight: 15,
          borderRadius: 4,
          boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.4)",
          background: "white",
        }}
      >
        <Dialog
          open={this.state.open}
          onClose={this.toggleClearDialog}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle id="form-dialog-title">
            {languages.schedule.clearField}
          </DialogTitle>
          <DialogContent style={{ width: 350, maxWidth: "97%" }}>
            <DialogContentText>
              {languages.schedule.clearFieldWarning}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.toggleClearDialog} color="primary">
              {languages.general.cancel}
            </Button>
            <Button
              onClick={this.handleClear}
              variant="contained"
              color="primary"
            >
              {languages.schedule.clear}
            </Button>
          </DialogActions>
        </Dialog>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: 50,
            paddingLeft: 10,
            background: this.props.theme.palette.primary.main,
            paddingRight: 5,
            borderRadius: "4px 4px 0px 0px",
          }}
        >
          {this.props.field.showSponsor && this.props.field.sponsorLogoURL && (
            <div>
              <StyledAvatar
                variant="rounded"
                alt=""
                src={this.props.field.sponsorLogoURL}
                style={{ width: 38, marginRight: 10 }}
              />
            </div>
          )}
          <div>
            <div>
              <Typography
                variant="body2"
                style={{ fontWeight: 500, color: "#ffffff", paddingRight: 5 }}
              >
                {this.props.field.name +
                  (this.props.field.showSponsor && this.props.field.sponsorName
                    ? " - " + this.props.field.sponsorName
                    : "")}
              </Typography>
            </div>
            <div>
              <Typography
                variant="body2"
                style={{ fontWeight: 400, color: "#ffffff" }}
              >
                {moment.unix(startTime).format("HH:mm") +
                  (endTime ? " - " + moment.unix(endTime).format("HH:mm") : "")}
              </Typography>
            </div>
          </div>
          <div style={{ flex: "1 1 10%" }} />
          <div>
            <IconButton
              aria-label="More"
              onClick={
                this.props.tournament.scheduleLocked || false
                  ? () => {}
                  : this.handleMenu
              }
              style={{
                marginBottom: 5,
              }}
            >
              {this.props.tournament.scheduleLocked || false ? (
                <LockIcon
                  style={{
                    height: 20,
                    width: 20,
                    position: "absolute",
                    top: 4,
                    color: "#ffffff",
                    marginRight: 5,
                    marginBottom: 5,
                  }}
                />
              ) : (
                <CreateIcon
                  style={{
                    height: 20,
                    width: 20,
                    position: "absolute",
                    top: 4,
                    color: "#ffffff",
                    marginRight: 5,
                  }}
                />
              )}
            </IconButton>
          </div>
          <EditFieldContainer
            handleMenuClose={this.handleClose}
            tournament={this.props.tournament}
            field={this.props.field}
            tournamentStatus={this.props.tournamentStatus}
            toggleFinishedDialog={this.props.toggleFinishedDialog}
            selectedSportPark={this.props.selectedSportPark}
            selectedDay={this.props.day}
            toggleDialog={this.toggleEditDialog}
            open={this.state.editOpen}
            poulesByKey={this.props.poulesByKey}
            userStatus={this.props.userStatus}
            user={this.props.user}
            toggleUpgradeDialog={this.props.toggleUpgradeDialog}
          />
          <Menu
            id="menu-more"
            anchorEl={this.state.anchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(this.state.anchorEl)}
            onClose={this.handleClose}
          >
            <MenuItem onClick={this.toggleEditDialog}>
              {languages.schedule.editField}
            </MenuItem>
            <MenuItem onClick={this.toggleClearDialog}>
              {languages.schedule.clearField}
            </MenuItem>
            <DeleteFieldContainer
              handleMenuClose={this.handleClose}
              tournament={this.props.tournament}
              field={this.props.field}
              matches={this.props.matches}
              days={this.props.days}
              tournamentStatus={this.props.tournamentStatus}
              toggleFinishedDialog={this.props.toggleFinishedDialog}
            />
          </Menu>
        </div>
        <Droppable droppableId={this.props.field.id}>
          {(provided, snapshot) => {
            return (
              <div ref={provided.innerRef} style={{ minHeight: 133 }}>
                {this.props.matches.length === 0 && (
                  <Typography
                    style={{
                      padding: "10px 5px 0",
                      whiteSpace: "normal",
                      color: "gray",
                      textAlign: "center",
                      width: 300,
                    }}
                  >
                    {languages.schedule.emptyFieldMessage}
                  </Typography>
                )}
                {this.props.matches.map((match, i) => {
                  return (
                    <Draggable
                      key={match.id}
                      draggableId={match.id}
                      index={i}
                      isDragDisabled={
                        this.props.tournament.scheduleLocked || false
                      }
                    >
                      {(provided, snapshot) => (
                        <div>
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            {match.isBreak ? (
                              <Break
                                tournament={this.props.tournament}
                                break={match}
                                tournamentStatus={this.props.tournamentStatus}
                                toggleFinishedDialog={
                                  this.props.toggleFinishedDialog
                                }
                                isEvent={match.isEvent || false}
                                startTime={match.startTime}
                                endTime={match.endTime}
                                dividedPerPoule={this.props.dividedPerPoule}
                                teams={this.props.teams}
                                teamsByKey={this.props.teamsByKey}
                                eventOptions={this.props.eventOptions}
                                poulesByKey={this.props.poulesByKey}
                                divisionsObject={this.props.divisionsObject}
                              />
                            ) : (
                              <Match
                                tournament={this.props.tournament}
                                match={match}
                                poulesByKey={this.props.poulesByKey}
                                bracketsByKey={this.props.bracketsByKey}
                                resultSpotsAll={this.props.resultSpotsAll}
                                divisions={this.props.divisions}
                                divisionsObject={this.props.divisionsObject}
                                refereesByKey={this.props.refereesByKey}
                                referees={this.props.referees}
                                startTime={match.startTime}
                                endTime={match.endTime}
                                dividedPerPoule={this.props.dividedPerPoule}
                                dividedRefs={this.props.dividedRefs}
                                tournamentStatus={this.props.tournamentStatus}
                                toggleFinishedDialog={
                                  this.props.toggleFinishedDialog
                                }
                                divisionColors={this.props.divisionColors}
                                groupColors={this.props.groupColors}
                                isDraggingRefereeFromMatch={
                                  this.props.isDraggingRefereeFromMatch
                                }
                                isDraggingReferee={this.props.isDraggingReferee}
                                selectedDay={this.props.selectedDay}
                                selectedSportPark={this.props.selectedSportPark}
                                allMatches={this.props.allMatches}
                                sportParksByKey={this.props.sportParksByKey}
                                isDraggingRefereeMaxMatchesWish={
                                  this.props.isDraggingRefereeMaxMatchesWish
                                }
                                teams={this.props.teams}
                                teamsByKey={this.props.teamsByKey}
                              />
                            )}
                          </div>
                          {provided.placeholder}
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            );
          }}
        </Droppable>
        <EditBreakContainer
          tournament={this.props.tournament}
          matches={this.props.matches}
          field={this.props.field}
          day={this.props.day}
          tournamentStatus={this.props.tournamentStatus}
          toggleFinishedDialog={this.props.toggleFinishedDialog}
          teams={this.props.teams}
          teamsByKey={this.props.teamsByKey}
          eventOptions={this.props.eventOptions}
          divisionsObject={this.props.divisionsObject}
          poulesByKey={this.props.poulesByKey}
        />
        <EditBreakContainer
          isEvent={true}
          tournament={this.props.tournament}
          matches={this.props.matches}
          field={this.props.field}
          day={this.props.day}
          tournamentStatus={this.props.tournamentStatus}
          toggleFinishedDialog={this.props.toggleFinishedDialog}
          teams={this.props.teams}
          teamsByKey={this.props.teamsByKey}
          eventOptions={this.props.eventOptions}
          divisionsObject={this.props.divisionsObject}
          poulesByKey={this.props.poulesByKey}
        />
      </div>
    );
  }
}

export default withTheme(Field);
